import { useState } from 'react'
import { Bot, Check, Search, TriangleAlert, X } from 'lucide-react'
import type { Spot as SpotBlok } from '../lib/types'

/* ===========================================================================
 *  De fout aanwijzen
 *
 *  Waarom dit een werkvorm is en geen lijstje
 *  ------------------------------------------
 *
 *  Je kunt opschrijven dat een chatbot dingen verzint. Iedereen knikt, en
 *  niemand herkent het de volgende dag -- want in het wild staat het
 *  verzinsel niet apart, het staat tussen vier zinnen die wél kloppen, in
 *  dezelfde rustige toon.
 *
 *  Dus laten we het zien zoals het eruitziet. Vijf zinnen, drie goed, twee
 *  verzonnen, en jij mag zeggen welke. Wie dat een keer heeft gedaan, kijkt
 *  daarna anders naar een normnummer.
 *
 *  Er zit geen strafpunt op het aanwijzen van iets wat wél klopt. Wie te
 *  voorzichtig wordt, leest niet meer -- en dat is niet het gedrag dat we
 *  willen aanleren.
 * =========================================================================== */

export default function Spot({
  blok, opKlaar,
}: {
  blok: SpotBlok
  opKlaar: () => void
}) {
  const [aangewezen, setAangewezen] = useState<Set<number>>(new Set())
  const [nagekeken, setNagekeken] = useState(false)

  function tik(i: number) {
    if (nagekeken) return
    setAangewezen((was) => {
      const nu = new Set(was)
      if (nu.has(i)) nu.delete(i); else nu.add(i)
      return nu
    })
  }

  function nakijken() {
    setNagekeken(true)
    opKlaar()
  }

  const verzinsels = blok.stukken.map((s, i) => (s.fout ? i : -1)).filter((i) => i >= 0)
  const gevonden = verzinsels.filter((i) => aangewezen.has(i)).length
  const teveel = [...aangewezen].filter((i) => !blok.stukken[i]?.fout).length

  return (
    <div className="spot">
      <p className="inleiding">{blok.inleiding}</p>

      <div className="chatvenster">
        <div className="chatkop">
          <Bot size={15} />
          <span>Antwoord van een chatbot</span>
          <span style={{ flex: 1 }} />
          <span className="stip3" /><span className="stip3" /><span className="stip3" />
        </div>

        <div className="chattekst">
          {blok.stukken.map((stuk, i) => {
            const gekozen = aangewezen.has(i)
            let klasse = 'spotstuk'
            if (nagekeken) {
              if (gekozen && stuk.fout) klasse += ' aangewezen-fout'
              else if (gekozen && !stuk.fout) klasse += ' aangewezen-goed'
              else if (!gekozen && stuk.fout) klasse += ' gemist'
            } else if (gekozen) {
              klasse += ' aangewezen-fout'
            }

            return (
              <button
                key={i}
                type="button"
                className={klasse}
                onClick={() => tik(i)}
                disabled={nagekeken}
              >
                {stuk.tekst}
              </button>
            )
          })}
        </div>
      </div>

      {!nagekeken ? (
        <button
          className="knop hoofd breed"
          onClick={nakijken}
          disabled={aangewezen.size === 0}
        >
          <Search size={17} />
          {aangewezen.size === 0
            ? 'Wijs eerst aan wat je niet vertrouwt'
            : `Nakijken (${aangewezen.size} aangewezen)`}
        </button>
      ) : (
        <div className="opkomst" style={{ display: 'grid', gap: 12 }}>
            <div className={`uitslag ${gevonden === verzinsels.length ? 'goed' : 'mis'}`}>
              {gevonden === verzinsels.length ? <Check size={18} /> : <TriangleAlert size={18} />}
              <span>
                <span className="titel">
                  Je vond {gevonden} van de {verzinsels.length} verzinsels
                  {teveel > 0 && ` (en wees ${teveel} stuk${teveel > 1 ? 'ken' : ''} aan die wél klopten)`}.
                </span>
                {blok.naderhand}
              </span>
            </div>

            <div className="uitlegjes">
              {blok.stukken.map((stuk, i) => (
                stuk.uitleg ? (
                  <div key={i} className={`uitslag ${stuk.fout ? 'mis' : 'grijs'}`}>
                    {stuk.fout ? <X size={16} /> : <Check size={16} />}
                    <span>
                      <span className="titel">
                        {kort(stuk.tekst)}
                      </span>
                      {stuk.uitleg}
                    </span>
                  </div>
                ) : null
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

/** De eerste woorden, zodat te zien is over welk stuk de uitleg gaat. */
function kort(tekst: string): string {
  const schoon = tekst.trim()
  return schoon.length > 58 ? `${schoon.slice(0, 55)}…` : schoon
}
